// frontend/src/hooks/useDebounce.ts

import { useState, useEffect, useCallback, useRef } from 'react'

// ============================================
// Базовый хук для дебаунса значения
// ============================================

/**
 * Хук для дебаунса значения
 * @param value - Значение для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Дебаунсированное значение
 * 
 * @example
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebounce(search, 500)
 * 
 * useEffect(() => {
 *   // Выполняется только через 500ms после последнего изменения
 *   fetchResults(debouncedSearch)
 * }, [debouncedSearch])
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

// ============================================
// Хук для дебаунса функции
// ============================================

/**
 * Хук для дебаунса функции
 * @param fn - Функция для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Дебаунсированная функция
 * 
 * @example
 * const handleSearch = useDebounceFn((query: string) => {
 *   fetchResults(query)
 * }, 500)
 * 
 * // Вызовется только через 500ms после последнего вызова
 * handleSearch('test')
 */
export function useDebounceFn<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 500
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        fn(...args)
        timeoutRef.current = null
      }, delay)
    },
    [fn, delay]
  )
}

// ============================================
// Хук для дебаунса с немедленным вызовом
// ============================================

/**
 * Хук для дебаунса с опцией немедленного вызова
 * @param fn - Функция для дебаунса
 * @param delay - Задержка в миллисекундах
 * @param immediate - Выполнять сразу, затем дебаунсить
 * @returns Дебаунсированная функция
 * 
 * @example
 * const handleSearch = useDebounceFnImmediate((query: string) => {
 *   // Вызовется сразу, затем через 500ms после последнего изменения
 *   fetchResults(query)
 * }, 500, true)
 */
export function useDebounceFnImmediate<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 500,
  immediate: boolean = false
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const calledRef = useRef<boolean>(false)

  return useCallback(
    (...args: Parameters<T>) => {
      if (immediate && !calledRef.current) {
        fn(...args)
        calledRef.current = true
        return
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        fn(...args)
        timeoutRef.current = null
        calledRef.current = false
      }, delay)
    },
    [fn, delay, immediate]
  )
}

// ============================================
// Хук для дебаунса с отменой
// ============================================

/**
 * Хук для дебаунса с возможностью отмены
 * @param fn - Функция для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Объект с дебаунсированной функцией и методом отмены
 * 
 * @example
 * const { debouncedFn, cancel } = useDebounceWithCancel((query: string) => {
 *   fetchResults(query)
 * }, 500)
 * 
 * // Отмена дебаунса
 * cancel()
 */
export function useDebounceWithCancel<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 500
): {
  debouncedFn: (...args: Parameters<T>) => void
  cancel: () => void
} {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        fn(...args)
        timeoutRef.current = null
      }, delay)
    },
    [fn, delay]
  )

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return { debouncedFn, cancel }
}

// ============================================
// Хук для дебаунса с троттлингом
// ============================================

/**
 * Хук для дебаунса с троттлингом (ограничение частоты вызовов)
 * @param fn - Функция для дебаунса
 * @param delay - Задержка в миллисекундах
 * @param options - Опции
 * @returns Дебаунсированная функция
 * 
 * @example
 * const handleScroll = useThrottle(() => {
 *   // Вызывается не чаще чем раз в 100ms
 *   handleScrollPosition()
 * }, 100)
 */
export function useThrottle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 100
): (...args: Parameters<T>) => void {
  const lastCallRef = useRef<number>(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now()

      if (now - lastCallRef.current >= delay) {
        fn(...args)
        lastCallRef.current = now
      } else if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          fn(...args)
          lastCallRef.current = Date.now()
          timeoutRef.current = null
        }, delay - (now - lastCallRef.current))
      }
    },
    [fn, delay]
  )
}

// ============================================
// Хук для дебаунса с загрузкой (loading state)
// ============================================

/**
 * Хук для дебаунса с индикатором загрузки
 * @param fn - Асинхронная функция для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Объект с функцией, состоянием загрузки и ошибкой
 * 
 * @example
 * const { execute, isLoading, error } = useDebounceAsync(
 *   async (query: string) => {
 *     return await fetchResults(query)
 *   },
 *   500
 * )
 * 
 * useEffect(() => {
 *   execute(searchQuery)
 * }, [searchQuery])
 */
export function useDebounceAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number = 500
): {
  execute: (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>> | null>
  isLoading: boolean
  error: Error | null
} {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingPromiseRef = useRef<{
    resolve: (value: Awaited<ReturnType<T>> | null) => void
    reject: (reason?: any) => void
  } | null>(null)

  const execute = useCallback(
    (...args: Parameters<T>): Promise<Awaited<ReturnType<T>> | null> => {
      return new Promise((resolve, reject) => {
        // Отменяем предыдущий таймаут
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // Сохраняем промис для разрешения после выполнения
        pendingPromiseRef.current = { resolve, reject }

        setIsLoading(true)
        setError(null)

        timeoutRef.current = setTimeout(async () => {
          try {
            const result = await fn(...args)
            setIsLoading(false)
            pendingPromiseRef.current?.resolve(result)
            pendingPromiseRef.current = null
            return result
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            setError(error)
            setIsLoading(false)
            pendingPromiseRef.current?.reject(error)
            pendingPromiseRef.current = null
            return null
          }
        }, delay)
      })
    },
    [fn, delay]
  )

  // Отмена при размонтировании
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (pendingPromiseRef.current) {
        pendingPromiseRef.current.reject(new Error('Component unmounted'))
        pendingPromiseRef.current = null
      }
    }
  }, [])

  return { execute, isLoading, error }
}

// ============================================
// Хук для дебаунса массива значений
// ============================================

/**
 * Хук для дебаунса массива значений (полезно для фильтров)
 * @param value - Массив значений для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Дебаунсированный массив
 * 
 * @example
 * const [filters, setFilters] = useState({ city: '', salary: 0 })
 * const debouncedFilters = useDebounceArray(filters, 500)
 * 
 * useEffect(() => {
 *   // Выполняется только через 500ms после последнего изменения
 *   applyFilters(debouncedFilters)
 * }, [debouncedFilters])
 */
export function useDebounceArray<T>(value: T[], delay: number = 500): T[] {
  const [debouncedValue, setDebouncedValue] = useState<T[]>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

// ============================================
// Хук для дебаунса объекта
// ============================================

/**
 * Хук для дебаунса объекта (полезно для форм)
 * @param value - Объект для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Дебаунсированный объект
 * 
 * @example
 * const [formData, setFormData] = useState({ name: '', email: '' })
 * const debouncedForm = useDebounceObject(formData, 500)
 * 
 * useEffect(() => {
 *   // Выполняется только через 500ms после последнего изменения
 *   saveForm(debouncedForm)
 * }, [debouncedForm])
 */
export function useDebounceObject<T extends Record<string, any>>(
  value: T,
  delay: number = 500
): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

// ============================================
// Хук для дебаунса с сохранением последнего значения
// ============================================

/**
 * Хук для дебаунса с сохранением последнего переданного значения
 * @param fn - Функция для дебаунса
 * @param delay - Задержка в миллисекундах
 * @returns Дебаунсированная функция и последнее вызванное значение
 * 
 * @example
 * const { debouncedFn, lastValue } = useDebounceWithLast(
 *   (query: string) => fetchResults(query),
 *   500
 * )
 * 
 * // lastValue содержит последний переданный аргумент
 */
export function useDebounceWithLast<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 500
): {
  debouncedFn: (...args: Parameters<T>) => void
  lastValue: Parameters<T>[0] | null
} {
  const [lastValue, setLastValue] = useState<Parameters<T>[0] | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      setLastValue(args[0] || null)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        fn(...args)
        timeoutRef.current = null
      }, delay)
    },
    [fn, delay]
  )

  return { debouncedFn, lastValue }
}

// ============================================
// Экспорт по умолчанию
// ============================================

export default useDebounce